import React, {CSSProperties, FC} from 'react'
import {useIntl} from 'react-intl'

/**
 * Placeholders for the server-side Teacher Allocation while a request is in flight, shaped like
 * the content they stand in for (progress card, grid table, conversation) and drawn with the
 * allocation design tokens so they follow light / dark mode. Decorative: each block announces a
 * single "Loading…" to screen readers.
 */

export const SkeletonBlock: FC<{
  width?: number | string
  height?: number | string
  round?: boolean
  style?: CSSProperties
}> = ({width = '100%', height = 14, round, style}) => (
  <span
    className={`ta-skeleton${round ? ' ta-skeleton--round' : ''}`}
    style={{width, height, ...style}}
    aria-hidden
  />
)

const LoadingLabel: FC = () => {
  const intl = useIntl()
  return (
    <span className='visually-hidden'>
      {intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.LOADING'})}
    </span>
  )
}

/** The landing page's progress card: title, remaining line and the bar. */
export const ProgressSkeleton: FC = () => (
  <div className='ta-skeleton-stack' role='status'>
    <SkeletonBlock width='55%' height={22} />
    <SkeletonBlock width='22%' height={14} />
    <SkeletonBlock height={12} round />
    <LoadingLabel />
  </div>
)

/** The allocation grid: a header row and section rows, at the table's own column widths. */
export const GridSkeleton: FC<{columns?: number; rows?: number}> = ({columns = 5, rows = 7}) => {
  const cols = Math.max(1, Math.min(columns, 8))
  return (
    <div className='ta-grid ta-grid--skeleton' role='status'>
      <table style={{width: 170 + cols * 210}}>
        <thead>
          <tr>
            <th className='ta-grid__corner'>
              <SkeletonBlock width='70%' />
            </th>
            {Array.from({length: cols}, (_, idx) => (
              <th key={idx} className='ta-grid__col-head'>
                <SkeletonBlock width={`${50 + ((idx * 17) % 35)}%`} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({length: rows}, (_, row) => (
            <tr key={row}>
              <th className='ta-grid__row-head'>
                <SkeletonBlock width='65%' />
              </th>
              {Array.from({length: cols}, (_, col) => (
                <td key={col} className='ta-grid__cell'>
                  <SkeletonBlock height={34} style={{borderRadius: 8}} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <LoadingLabel />
    </div>
  )
}

/** Versi's thread while the saved conversation is read: assistant and user bubbles. */
export const ChatSkeleton: FC = () => (
  <div className='ta-skeleton-chat' role='status'>
    <div className='ta-skeleton-chat__msg'>
      <SkeletonBlock width={36} height={36} round />
      <div className='ta-skeleton-stack' style={{flex: 1}}>
        <SkeletonBlock width='70%' />
        <SkeletonBlock width='45%' />
      </div>
    </div>
    <SkeletonBlock width='80%' height={44} style={{borderRadius: 12}} />
    <SkeletonBlock width='80%' height={44} style={{borderRadius: 12}} />
    <div className='ta-skeleton-chat__msg ta-skeleton-chat__msg--user'>
      <SkeletonBlock width='40%' height={36} style={{borderRadius: 12}} />
    </div>
    <LoadingLabel />
  </div>
)

/** A filter dropdown's place while the server resolves the Branch / Level. */
export const SelectSkeleton: FC = () => (
  <SkeletonBlock width={170} height={40} style={{borderRadius: 10}} />
)

/** Paragraph lines (a confirmation dialog whose preview is loading). */
export const TextSkeleton: FC<{lines?: number}> = ({lines = 3}) => (
  <div className='ta-skeleton-stack' role='status'>
    {Array.from({length: lines}, (_, idx) => (
      <SkeletonBlock key={idx} width={idx === lines - 1 ? '60%' : '100%'} />
    ))}
    <LoadingLabel />
  </div>
)
